'use client';

import { motion } from "motion/react";
import { Card, Skeleton } from "@heroui/react";

const SkeletonCard = () => {
  return (
    <Card className="h-full border border-neutral-200/60 dark:border-zinc-800/60 bg-white/40 dark:bg-zinc-950/30 backdrop-blur-md rounded-[24px] p-3 gap-4 flex flex-col">
      {/* Image Placeholder */}
      <Skeleton className="w-full h-48 rounded-2xl bg-neutral-200 dark:bg-zinc-800" />

      {/* Content Placeholder */}
      <div className="px-3 pb-3 flex-grow flex flex-col justify-between gap-4">
        <div className="space-y-3">
          {/* Metadata Row */}
          <div className="flex gap-3">
            <Skeleton className="w-16 h-3 rounded-full bg-neutral-200 dark:bg-zinc-800" />
            <Skeleton className="w-20 h-3 rounded-full bg-neutral-200 dark:bg-zinc-800" />
          </div>
          {/* Title Lines */}
          <div className="space-y-2">
            <Skeleton className="w-11/12 h-5 rounded-lg bg-neutral-200 dark:bg-zinc-800" />
            <Skeleton className="w-2/3 h-5 rounded-lg bg-neutral-200 dark:bg-zinc-800" />
          </div>
          {/* Excerpt Lines */}
          <div className="space-y-1.5 pt-1">
            <Skeleton className="w-full h-3.5 rounded-md bg-neutral-200/70 dark:bg-zinc-800/70" />
            <Skeleton className="w-5/6 h-3.5 rounded-md bg-neutral-200/70 dark:bg-zinc-800/70" />
          </div>
        </div>

        {/* Footer Row */}
        <div className="flex justify-between items-center pt-3 border-t border-neutral-150/40 dark:border-zinc-800/40">
          <div className="flex items-center gap-2">
            <Skeleton className="w-6 h-6 rounded-full bg-neutral-200 dark:bg-zinc-800" />
            <Skeleton className="w-16 h-3 rounded-md bg-neutral-200 dark:bg-zinc-800" />
          </div>
          <Skeleton className="w-16 h-4 rounded-md bg-neutral-200 dark:bg-zinc-800" />
        </div>
      </div>
    </Card>
  );
};

const SkeletonFeatured = () => {
  return (
    <Card className="w-full border border-neutral-200/60 dark:border-zinc-800/60 bg-white/40 dark:bg-zinc-950/30 backdrop-blur-md rounded-[28px] p-4 lg:p-6 mb-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* Large Image Column */}
        <div className="lg:col-span-7 h-64 lg:h-96 w-full relative">
          <Skeleton className="w-full h-full rounded-2xl bg-neutral-200 dark:bg-zinc-800" />
        </div>
        {/* Content Column */}
        <div className="lg:col-span-5 flex flex-col justify-between py-2 space-y-6">
          <div className="space-y-4">
            {/* Category tag */}
            <Skeleton className="w-24 h-6 rounded-full bg-neutral-200 dark:bg-zinc-800" />
            {/* Metadata */}
            <div className="flex items-center gap-3">
              <Skeleton className="w-20 h-3.5 rounded-full bg-neutral-200 dark:bg-zinc-800" />
              <Skeleton className="w-24 h-3.5 rounded-full bg-neutral-200 dark:bg-zinc-800" />
            </div>
            {/* Title */}
            <div className="space-y-3">
              <Skeleton className="w-full h-7 rounded-xl bg-neutral-200 dark:bg-zinc-800" />
              <Skeleton className="w-11/12 h-7 rounded-xl bg-neutral-200 dark:bg-zinc-800" />
              <Skeleton className="w-4/5 h-7 rounded-xl bg-neutral-200 dark:bg-zinc-800" />
            </div>
            {/* Excerpt */}
            <div className="space-y-2 pt-2">
              <Skeleton className="w-full h-4 rounded-lg bg-neutral-200/70 dark:bg-zinc-800/70" />
              <Skeleton className="w-11/12 h-4 rounded-lg bg-neutral-200/70 dark:bg-zinc-800/70" />
              <Skeleton className="w-5/6 h-4 rounded-lg bg-neutral-200/70 dark:bg-zinc-800/70" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-150/40 dark:border-zinc-800/40">
            <div className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-zinc-800" />
              <Skeleton className="w-24 h-4 rounded-md bg-neutral-200 dark:bg-zinc-800" />
            </div>
            <Skeleton className="w-24 h-6 rounded-lg bg-neutral-200 dark:bg-zinc-800" />
          </div>
        </div>
      </div>
    </Card>
  );
};

const BlogPageSkeleton = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 260,
        damping: 25,
      },
    },
  };

  return (
    <div className="min-h-screen w-full bg-transparent p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Title skeleton */}
        <div className="text-center mb-16 space-y-4">
          <Skeleton className="w-56 h-14 rounded-2xl mx-auto mb-2" />
          <Skeleton className="w-96 h-5 rounded-lg mx-auto" />
        </div>

        {/* Filter categories tabs skeleton */}
        <div className="flex items-center justify-center gap-2 mb-12 overflow-x-auto pb-2 scrollbar-hidden">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="w-24 h-9 rounded-full shrink-0 bg-neutral-200 dark:bg-zinc-800" />
          ))}
        </div>

        {/* Featured Post skeleton */}
        <SkeletonFeatured />

        {/* Grid skeleton */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {[...Array(6)].map((_, i) => (
            <motion.div key={i} variants={itemVariants}>
              <SkeletonCard />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPageSkeleton;



